import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import {
  View,
  TextInput,
  Text,
  TouchableOpacity,
  ScrollView,
} from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import {
  useFloating,
  autoUpdate,
  offset,
  flip,
  shift,
  useRole,
  useInteractions,
  FloatingPortal,
  FloatingFocusManager,
} from "@floating-ui/react";
import { observer } from "mobx-react";
import Colors from "../../Resources/Color";
import { useTranslation } from "react-i18next";
import { useScreenDimensions, useDevice } from "../_layout";
import { useCoachStore } from "../contexts/CoachStoreContext";
import { getExerciseSearchService } from "../Classes/ExerciseSearchService";
import Attribute from "../Firestore/Attribute";
import Tooltip from "../Components/Tooltip";
import CustomActivityIndicator from "../Components/CustomActivityIndicator";
import ExerciseManualPickerModal from "./ExerciseManualPickerModal";

/**
 * ExerciseSearchFloating
 *
 * A floating search component that displays exercise and modifier suggestions
 * with intelligent parsing of compound exercise names (e.g., "Bench Press 1ct Pause Close Grip").
 *
 * Features:
 * - Real-time search with debouncing
 * - Keyboard navigation (Arrow Up/Down, Enter, Escape)
 * - Highlighted matching words in suggestions
 * - Pills showing main exercise type and modifiers
 * - Category-aware modifier suggestions
 * - Managed by Coach store to ensure only one floating search is open at a time
 */

// Color constants for pills
const PILL_COLORS = {
  exercise: {
    background: "rgba(63, 123, 141, 0.12)",
    text: Colors.bubbleColor,
  },
  modifier: {
    background: "rgba(156, 156, 156, 0.12)",
    text: Colors.darkgrayColor,
  },
};

const buildExerciseNameWithModifiers = (exerciseName, modifiers, language) => {
  if (!modifiers || modifiers.length === 0) {
    return exerciseName;
  }

  let selectedEquipment = "";
  let selectedTempo = "";
  let selectedROM = "";
  let selectedStanceGrip = "";
  let selectedLoadAccomodation = "";
  let selectedAccessories = "";
  let selectedOther = "";

  if (language === "es") {
    modifiers.forEach((modifier) => {
      const modifierName = modifier.name;
      switch (modifier.type) {
        case Attribute.AttributeTitles.equipment:
          selectedEquipment = ` con ${modifierName}`;
          break;
        case Attribute.AttributeTitles.rom:
          selectedROM = ` ${modifierName}`;
          break;
        case Attribute.AttributeTitles.tempo:
          selectedTempo = ` ${modifierName}`;
          break;
        case Attribute.AttributeTitles.stanceGrip:
          selectedStanceGrip = ` ${modifierName}`;
          break;
        case Attribute.AttributeTitles.loadAccomodation:
          selectedLoadAccomodation += ` ${modifierName}`;
          break;
        case Attribute.AttributeTitles.accessories:
          selectedAccessories += ` ${modifierName}`;
          break;
        default:
          selectedOther += ` ${modifierName}`;
      }
    });

    return `${exerciseName}${selectedTempo}${selectedROM}${selectedStanceGrip}${selectedLoadAccomodation}${selectedAccessories}${selectedOther}${selectedEquipment}`;
  }

  modifiers.forEach((modifier) => {
    const modifierName = modifier.name;
    switch (modifier.type) {
      case Attribute.AttributeTitles.equipment:
        selectedEquipment = `${modifierName} `;
        break;
      case Attribute.AttributeTitles.rom:
        selectedROM = `${modifierName} `;
        break;
      case Attribute.AttributeTitles.tempo:
        selectedTempo = `${modifierName} `;
        break;
      case Attribute.AttributeTitles.stanceGrip:
        selectedStanceGrip = `${modifierName} `;
        break;
      case Attribute.AttributeTitles.loadAccomodation:
        selectedLoadAccomodation += `${modifierName} `;
        break;
      case Attribute.AttributeTitles.accessories:
        selectedAccessories += `${modifierName} `;
        break;
      default:
        selectedOther += `${modifierName} `;
    }
  });

  return `${selectedEquipment}${selectedTempo}${selectedROM}${selectedStanceGrip}${selectedLoadAccomodation}${selectedAccessories}${selectedOther}${exerciseName}`;
};

/**
 * Pill component for displaying exercise type or modifier
 */
const Pill = ({ label, type = "modifier" }) => {
  const colors = PILL_COLORS[type] || PILL_COLORS.modifier;

  return (
    <View
      style={{
        backgroundColor: colors.background,
        paddingHorizontal: 8,
        paddingVertical: 3,
        borderRadius: 8,
        maxWidth: "100%",
        flexShrink: 1,
        minWidth: 0,
        alignSelf: "flex-start",
      }}
    >
      <Text
        style={{
          fontSize: 11,
          fontWeight: "500",
          color: colors.text,
          flexShrink: 1,
        }}
        numberOfLines={2}
        ellipsizeMode="tail"
      >
        {label}
      </Text>
    </View>
  );
};

/**
 * Highlighted text component that underlines matching phrases
 * Uses greedy longest-match-first algorithm for phrase matching
 */
const HighlightedText = ({ text, searchQuery }) => {
  if (!searchQuery || !searchQuery.trim()) {
    return (
      <Text style={{ fontSize: 13, color: Colors.blackColor }}>{text}</Text>
    );
  }

  // Normalize function for comparison (case and accent insensitive)
  const normalize = (str) =>
    str
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "");

  const normalizedText = normalize(text);
  const normalizedQuery = normalize(searchQuery.trim());

  // Generate all possible substrings from the query (sorted by length, longest first)
  const generateQuerySubstrings = (query) => {
    const substrings = [];
    const words = query.split(/\s+/);

    // Generate all contiguous subsequences of words
    for (let length = words.length; length >= 1; length--) {
      for (let start = 0; start <= words.length - length; start++) {
        const substring = words.slice(start, start + length).join(" ");
        if (substring) {
          substrings.push(substring);
        }
      }
    }

    return substrings;
  };

  // Important: ensure longer matches win over shorter prefixes.
  // Example: query contains "bar" and "barbell"; we must match "barbell" first
  // so that "bar" doesn't consume the overlap and prevent highlighting "bell".
  const querySubstrings = generateQuerySubstrings(normalizedQuery).sort(
    (a, b) => {
      const aWordCount = a.split(/\s+/).length;
      const bWordCount = b.split(/\s+/).length;

      if (bWordCount !== aWordCount) {
        return bWordCount - aWordCount;
      }

      if (b.length !== a.length) {
        return b.length - a.length;
      }

      return a.localeCompare(b);
    }
  );

  // Track which character positions should be underlined
  const underlinePositions = new Set();

  // Track which query substrings have been matched (to avoid duplicate matches)
  const matchedSubstrings = new Set();

  // Greedy matching: try longest matches first
  for (const substring of querySubstrings) {
    if (matchedSubstrings.has(substring)) {
      continue;
    }

    // Find all occurrences of this substring in the text
    let searchIndex = 0;
    while (searchIndex < normalizedText.length) {
      const foundIndex = normalizedText.indexOf(substring, searchIndex);

      if (foundIndex === -1) {
        break;
      }

      // Check if this range overlaps with already matched positions
      let hasOverlap = false;
      for (let i = foundIndex; i < foundIndex + substring.length; i++) {
        if (underlinePositions.has(i)) {
          hasOverlap = true;
          break;
        }
      }

      // If no overlap, mark this range as matched
      if (!hasOverlap) {
        for (let i = foundIndex; i < foundIndex + substring.length; i++) {
          underlinePositions.add(i);
        }
        matchedSubstrings.add(substring);
      }

      searchIndex = foundIndex + 1;
    }
  }

  // Build the text with underlined characters
  return (
    <Text style={{ fontSize: 13, color: Colors.blackColor }}>
      {text.split("").map((char, index) => {
        if (underlinePositions.has(index)) {
          return (
            <Text
              key={index}
              style={{
                textDecorationLine: "underline",
                textDecorationColor: Colors.bubbleColor,
                textDecorationStyle: "solid",
              }}
            >
              {char}
            </Text>
          );
        }
        return <Text key={index}>{char}</Text>;
      })}
    </Text>
  );
};

const getScrollableNodeFromRef = (scrollContainerRef) => {
  const refCurrent = scrollContainerRef?.current;
  if (!refCurrent) {
    return null;
  }

  // React Native Web ScrollView exposes getScrollableNode() which returns the actual DOM element.
  const scrollableNode = refCurrent.getScrollableNode?.();
  if (scrollableNode) {
    return scrollableNode;
  }

  // If ref already points to a DOM element, it should have scrollTop.
  if (refCurrent.scrollTop !== undefined) {
    return refCurrent;
  }

  // Fallback for cases where the ref points to a wrapper element.
  const firstChild = refCurrent.children?.[0];
  if (firstChild && firstChild.scrollTop !== undefined) {
    return firstChild;
  }

  return null;
};

const isUsableReferenceElement = (referenceElement) => {
  if (!referenceElement) {
    return false;
  }

  // On web, refs usually point to DOM elements.
  const isDomElement =
    typeof Element !== "undefined" && referenceElement instanceof Element;

  if (isDomElement && referenceElement.isConnected === false) {
    return false;
  }

  if (typeof window !== "undefined") {
    const getRect = referenceElement.getBoundingClientRect;
    if (typeof getRect === "function") {
      const rect = getRect.call(referenceElement);
      // If the element is effectively not laid out (common when view is hidden),
      // FloatingUI can fall back to (0,0). Treat this as unusable.
      if (!rect || (rect.width === 0 && rect.height === 0)) {
        return false;
      }
    }
  }

  return true;
};

const isEditableEventTarget = (target) => {
  if (!target || typeof window === "undefined") {
    return false;
  }

  const el = target;

  // If this is a text-editable element, do not hijack arrow/enter keys.
  const tagName = (el.tagName || "").toLowerCase();
  if (tagName === "input" || tagName === "textarea" || tagName === "select") {
    return true;
  }

  if (el.isContentEditable) {
    return true;
  }

  return false;
};

/**
 * Recently Used Row component
 */
const RecentlyUsedRow = ({
  exercise,
  isSelected,
  onSelect,
  onHover,
  t,
  language,
  index,
  totalResults,
  scrollContainerRef,
  keyboardNavigationRef,
}) => {
  const rowRef = useRef(null);

  // Build the full exercise name
  const fullName = useMemo(() => {
    return exercise.getName(t, language);
  }, [exercise, t, language]);

  // Scroll into view when selected
  useEffect(() => {
    if (
      isSelected &&
      keyboardNavigationRef?.current &&
      rowRef.current &&
      scrollContainerRef?.current
    ) {
      const isFirstItem = index === 0;
      const isLastItem = index === totalResults - 1;
      const rowElement = rowRef.current;

      const scrollContainer = getScrollableNodeFromRef(scrollContainerRef);

      if (isFirstItem) {
        scrollContainerRef.current?.scrollTo?.({ y: 0, animated: true });
        if (scrollContainer) {
          scrollContainer.scrollTop = 0;
        }
        return;
      }

      if (isLastItem) {
        scrollContainerRef.current?.scrollToEnd?.({ animated: true });
        if (scrollContainer) {
          const containerHeight = scrollContainer.clientHeight;
          const scrollHeight = scrollContainer.scrollHeight;
          scrollContainer.scrollTop = scrollHeight - containerHeight;
        }
        return;
      }

      rowElement.scrollIntoView?.({
        block: "nearest",
        behavior: "smooth",
      });
    }
  }, [
    isSelected,
    index,
    totalResults,
    scrollContainerRef,
    keyboardNavigationRef,
  ]);

  return (
    <TouchableOpacity
      ref={rowRef}
      onPress={onSelect}
      onMouseEnter={onHover}
      style={{
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
        paddingVertical: 10,
        paddingHorizontal: 12,
        marginHorizontal: 8,
        borderRadius: 10,
        backgroundColor: isSelected
          ? "rgba(63, 123, 141, 0.08)"
          : "transparent",
        outlineStyle: "none",
      }}
    >
      {/* Left: Exercise name */}
      <View
        style={{
          flexGrow: 1,
          flexShrink: 1,
          marginRight: 10,
          minWidth: 0,
        }}
      >
        <Text style={{ fontSize: 13, color: Colors.blackColor }}>
          {fullName}
        </Text>
      </View>

      {/* Right: Clock icon to indicate recently used */}
      <View
        style={{
          flexDirection: "row",
          alignItems: "center",
          gap: 6,
        }}
      >
        <Ionicons name="time-outline" size={14} color={Colors.grayColor} />
      </View>
    </TouchableOpacity>
  );
};

/**
 * Suggestion Row component
 */
const SuggestionRow = ({
  result,
  isSelected,
  onSelect,
  onHover,
  searchQuery,
  t,
  language,
  index,
  totalResults,
  scrollContainerRef,
  keyboardNavigationRef,
}) => {
  const rowRef = useRef(null);

  // Build the full exercise name with modifiers using getName logic
  const fullName = useMemo(() => {
    return buildExerciseNameWithModifiers(
      result.exerciseName,
      result.modifiers,
      language
    );
  }, [result.exerciseName, result.modifiers, language]);

  // Scroll into view when selected
  useEffect(() => {
    if (
      isSelected &&
      keyboardNavigationRef?.current &&
      rowRef.current &&
      scrollContainerRef?.current
    ) {
      const isFirstItem = index === 0;
      const isLastItem = index === totalResults - 1;
      const rowElement = rowRef.current;

      const scrollContainer = getScrollableNodeFromRef(scrollContainerRef);

      if (isFirstItem) {
        scrollContainerRef.current?.scrollTo?.({ y: 0, animated: true });
        if (scrollContainer) {
          scrollContainer.scrollTop = 0;
        }
        return;
      }

      if (isLastItem) {
        scrollContainerRef.current?.scrollToEnd?.({ animated: true });
        if (scrollContainer) {
          const containerHeight = scrollContainer.clientHeight;
          const scrollHeight = scrollContainer.scrollHeight;
          scrollContainer.scrollTop = scrollHeight - containerHeight;
        }
        return;
      }

      rowElement.scrollIntoView?.({
        block: "nearest",
        behavior: "smooth",
      });
    }
  }, [
    isSelected,
    index,
    totalResults,
    scrollContainerRef,
    keyboardNavigationRef,
  ]);

  return (
    <TouchableOpacity
      ref={rowRef}
      onPress={onSelect}
      onMouseEnter={onHover}
      style={{
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
        paddingVertical: 10,
        paddingHorizontal: 12,
        marginHorizontal: 8,
        borderRadius: 10,
        backgroundColor: isSelected
          ? "rgba(63, 123, 141, 0.08)"
          : "transparent",
        outlineStyle: "none",
      }}
    >
      {/* Left: Exercise name with highlighted matching characters */}
      <View
        style={{
          flexGrow: 1,
          flexShrink: 1,
          marginRight: 10,
          minWidth: 0,
        }}
      >
        <HighlightedText text={fullName} searchQuery={searchQuery} />
      </View>

      {/* Right: Pills */}
      <View
        style={{
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "flex-end",
          flexWrap: "wrap",
          gap: 6,
          flexShrink: 1,
          minWidth: 0,
        }}
      >
        {/* Main exercise name pill */}
        <Pill label={result.exerciseName} type="exercise" />

        {/* Modifier pills */}
        {result.modifiers &&
          result.modifiers.map((modifier, idx) => (
            <Pill
              key={modifier.id || idx}
              label={modifier.name}
              type="modifier"
            />
          ))}
      </View>
    </TouchableOpacity>
  );
};

const DeleteConfirmationView = ({
  deleteTitle,
  deleteText,
  onCancel,
  onConfirm,
  t,
  isMobile,
}) => {
  const [cancelHovered, setCancelHovered] = useState(false);
  const [deleteHovered, setDeleteHovered] = useState(false);

  return (
    <View
      style={{
        padding: 20,
        gap: 16,
      }}
    >
      <Text
        style={{
          fontSize: 16,
          fontWeight: "600",
          color: Colors.blackColor,
        }}
      >
        {deleteTitle}
      </Text>
      <Text
        style={{
          fontSize: 13,
          color: Colors.blackColor,
          lineHeight: 18,
        }}
      >
        {deleteText}
      </Text>

      <View
        style={{
          flexDirection: "row",
          gap: 10,
          justifyContent: "flex-end",
          marginTop: 8,
        }}
      >
        <TouchableOpacity
          onPress={() => {
            setCancelHovered(false);
            onCancel?.();
          }}
          onMouseEnter={() => !isMobile && setCancelHovered(true)}
          onMouseLeave={() => !isMobile && setCancelHovered(false)}
          style={{
            paddingHorizontal: 16,
            paddingVertical: 8,
            borderRadius: 12,
            borderColor: cancelHovered
              ? Colors.darkgrayColor
              : Colors.grayColor,
            backgroundColor: cancelHovered ? Colors.hoveredGray : "transparent",
            outlineStyle: "none",
            borderWidth: 1,
            transition: "all 0.15s ease",
          }}
        >
          <Text
            style={{
              fontSize: 13,
              fontWeight: "500",
              color: Colors.darkgrayColor,
            }}
          >
            {t("cancel-string")}
          </Text>
        </TouchableOpacity>

        <TouchableOpacity
          onPress={() => {
            setDeleteHovered(false);
            onConfirm?.();
          }}
          onMouseEnter={() => !isMobile && setDeleteHovered(true)}
          onMouseLeave={() => !isMobile && setDeleteHovered(false)}
          style={{
            paddingHorizontal: 16,
            paddingVertical: 8,
            borderRadius: 12,
            backgroundColor: Colors.redColor,
            opacity: deleteHovered ? 0.92 : 1,
            outlineStyle: "none",
            transition: "opacity 0.15s ease",
          }}
        >
          <Text
            style={{
              fontSize: 13,
              fontWeight: "500",
              color: Colors.whiteColor,
            }}
          >
            {t("delete-string")}
          </Text>
        </TouchableOpacity>
      </View>
    </View>
  );
};

const ExerciseSearchFloating = observer(
  ({
    isOpen,
    onClose,
    referenceElement,
    onSelectExercise,
    instanceId,
    suggestionContext,
    initialSearchText = "",
    slotExercise = null,
    primarySlotExercise = null,
    block = null,
    protocolSlot = null,
    currentWeek = null,
  }) => {
    const { t, i18n } = useTranslation();
    const language = i18n.language;
    const { screenSize } = useScreenDimensions();
    const { isMobile } = useDevice();
    const coachStore = useCoachStore();
    const insets = useSafeAreaInsets();

    const MANUAL_HOVER_COLOR = "#2E5E6E";

    const [searchQuery, setSearchQuery] = useState("");
    const [results, setResults] = useState([]);
    const [recentlyUsedExercises, setRecentlyUsedExercises] = useState([]);
    const [selectedIndex, setSelectedIndex] = useState(0);
    const [isSearching, setIsSearching] = useState(false);
    const [deleteButtonHovered, setDeleteButtonHovered] = useState(false);
    const [closeButtonHovered, setCloseButtonHovered] = useState(false);
    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
    const [showManualPicker, setShowManualPicker] = useState(false);
    const [manualIconHovered, setManualIconHovered] = useState(false);
    const [clearIconHovered, setClearIconHovered] = useState(false);
    const [manualLinkFooterHovered, setManualLinkFooterHovered] =
      useState(false);
    const [manualLinkEmptyHovered, setManualLinkEmptyHovered] = useState(false);

    // Track the *visual* viewport height (best signal for on-screen keyboards on mobile browsers).
    // Fallback to window.innerHeight when VisualViewport isn't available.
    const [visualViewportHeight, setVisualViewportHeight] = useState(null);

    const searchInputRef = useRef(null);
    const debounceTimerRef = useRef(null);
    const scrollViewRef = useRef(null);
    const initialSearchTextRef = useRef(""); // Store the initial search text
    const keyboardNavigationRef = useRef(false);

    const searchService = useMemo(() => getExerciseSearchService(), []);

    // Get predefined data
    const predefinedExercises = useMemo(
      () => coachStore.customPredefinedExercises,
      [coachStore]
    );
    const predefinedAttributes = useMemo(
      () => coachStore.customPredefinedAttributes,
      [coachStore]
    );

    // Floating UI setup
    const { refs, floatingStyles, context, update } = useFloating({
      open: isOpen,
      onOpenChange: (open) => {
        // This is triggered by FloatingUI interactions (e.g., clicking outside)
        if (!open) {
          onClose();
        }
      },
      strategy: "fixed",
      placement: "bottom-start",
      middleware: [
        offset(6),
        flip({
          fallbackAxisSideDirection: "end",
          padding: 8,
        }),
        shift({ padding: 8, crossAxis: true }),
      ],
      whileElementsMounted: autoUpdate,
    });

    useEffect(() => {
      if (!isOpen || !isMobile) {
        setVisualViewportHeight(null);
        return;
      }

      if (typeof window === "undefined") {
        return;
      }

      const vv = window.visualViewport;

      const updateViewport = () => {
        const nextHeight = vv?.height ?? window.innerHeight;
        setVisualViewportHeight(nextHeight);
        update?.();
      };

      updateViewport();

      // Mobile only: listen to visual viewport changes (keyboard open/close).
      if (vv) {
        vv.addEventListener("resize", updateViewport);
        vv.addEventListener("scroll", updateViewport);
        return () => {
          vv.removeEventListener("resize", updateViewport);
          vv.removeEventListener("scroll", updateViewport);
        };
      }

      // Fallback for browsers without VisualViewport.
      window.addEventListener("resize", updateViewport);
      return () => window.removeEventListener("resize", updateViewport);
    }, [isOpen, isMobile, update]);

    const floatingContainerStyle = useMemo(() => {
      const baseStyleCommon = {
        backgroundColor: Colors.whiteColor,
        borderRadius: 14,
        borderWidth: 1,
        borderColor: Colors.borderColor,
        outlineStyle: "none",
        shadowColor: "#000",
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.1,
        shadowRadius: 12,
        overflow: "hidden",
        minWidth: screenSize === "small" ? 320 : 400,
      };

      const defaultMaxHeight = 302;
      const defaultMinHeight = showDeleteConfirmation ? 0 : 180;

      let maxHeight = defaultMaxHeight;
      let minHeight = defaultMinHeight;

      let viewportHeight = null;
      let viewportMaxHeight = null;
      let safeArea = 0;
      const viewportPadding = 12;

      if (isMobile) {
        viewportHeight =
          visualViewportHeight ??
          (typeof window !== "undefined" ? window.innerHeight : null);
        safeArea = (insets?.top || 0) + (insets?.bottom || 0);
        viewportMaxHeight = viewportHeight
          ? Math.max(120, viewportHeight - safeArea - viewportPadding)
          : null;

        if (viewportMaxHeight) {
          maxHeight = Math.min(defaultMaxHeight, viewportMaxHeight);
          minHeight = showDeleteConfirmation ? 0 : Math.min(180, maxHeight);
        }
      }

      const baseStyle = {
        ...baseStyleCommon,
        minHeight,
        maxHeight,
      };

      if (showManualPicker) {
        const manualHeight =
          isMobile && viewportHeight
            ? Math.round((viewportHeight - safeArea - viewportPadding) * 0.96)
            : "96vh";

        const manualMaxHeight = isMobile
          ? viewportMaxHeight
            ? viewportMaxHeight
            : "100%"
          : 440;

        return {
          ...baseStyle,
          width: "92vw",
          maxWidth: 600,
          height: manualHeight,
          maxHeight: manualMaxHeight,
        };
      }

      return {
        ...baseStyle,
        maxWidth: screenSize === "small" ? "96%" : 600,
      };
    }, [
      insets?.bottom,
      insets?.top,
      isMobile,
      screenSize,
      showDeleteConfirmation,
      showManualPicker,
      visualViewportHeight,
    ]);

    const role = useRole(context);
    const { getFloatingProps } = useInteractions([role]);

    const resetAndClose = useCallback(() => {
      setSearchQuery("");
      setResults([]);
      setRecentlyUsedExercises([]);
      setSelectedIndex(0);
      setShowDeleteConfirmation(false);
      setShowManualPicker(false);
      onClose?.();
    }, [onClose]);

    // Set reference element
    useEffect(() => {
      if (isUsableReferenceElement(referenceElement)) {
        refs.setReference(referenceElement);
        return;
      }

      // Clear reference if it becomes unavailable (e.g., navigation away).
      refs.setReference(null);
    }, [referenceElement, refs]);

    // If opened without a usable reference element, close immediately.
    // This avoids the floating rendering at the top-left when the anchor is off-screen.
    useEffect(() => {
      if (!isOpen) {
        return;
      }

      let cancelled = false;

      const check = () => {
        if (cancelled) {
          return;
        }

        if (!isUsableReferenceElement(referenceElement)) {
          onClose?.();
          return;
        }

        update?.();
      };

      if (typeof requestAnimationFrame !== "undefined") {
        const rafId = requestAnimationFrame(check);
        return () => {
          cancelled = true;
          cancelAnimationFrame(rafId);
        };
      }

      const timeoutId = setTimeout(check, 0);
      return () => {
        cancelled = true;
        clearTimeout(timeoutId);
      };
    }, [isOpen, referenceElement, onClose, update]);

    useEffect(() => {
      update?.();
    }, [showManualPicker, update]);

    // Handle isOpen changes (when parent opens/closes the floating search)
    useEffect(() => {
      if (!isOpen) {
        // Reset state when closed
        setSearchQuery("");
        setResults([]);
        setRecentlyUsedExercises([]);
        setSelectedIndex(0);
        setShowManualPicker(false);
        initialSearchTextRef.current = "";
      } else {
        // When opening, check for recently used first, then show suggestions
        if (block && suggestionContext) {
          const recentExercises = searchService.getRecentlyUsedExercises(
            block,
            suggestionContext,
            protocolSlot,
            currentWeek,
            slotExercise
          );

          console.log("Recently used exercises found:", recentExercises.length);

          if (recentExercises.length > 0) {
            setRecentlyUsedExercises(recentExercises);
            setResults([]);
            return;
          }
        }

        // Fall back to suggestions if no recent exercises
        if (suggestionContext) {
          console.log("Loading default suggestions for:", suggestionContext);
          const suggestions = searchService.getSuggestions(
            suggestionContext,
            predefinedExercises,
            slotExercise
          );
          setResults(suggestions);
          setRecentlyUsedExercises([]);
        }
      }
    }, [
      isOpen,
      block,
      suggestionContext,
      protocolSlot,
      currentWeek,
      searchService,
      predefinedExercises,
    ]);

    // Focus input and set initial search text when opened
    useEffect(() => {
      if (isOpen) {
        if (initialSearchText) {
          // Store the initial search text
          initialSearchTextRef.current = initialSearchText;
          setSearchQuery(initialSearchText);
        }

        // NOTE: On mobile browsers (especially iOS Safari), programmatic focus
        // from effects/timeouts is often blocked unless it happens directly
        // inside the user's tap event. Attempting it can also cause keyboard
        // flicker (opens then immediately closes). So we only auto-focus on
        // non-mobile; mobile users can tap the input to focus.
        if (!isMobile && searchInputRef.current) {
          setTimeout(() => {
            searchInputRef.current?.focus();
          }, 50);
        }
      }
    }, [isOpen, initialSearchText, isMobile]);

    // Perform search with debouncing
    const performSearch = useCallback(
      (query) => {
        const trimmedQuery = query.trim();

        // Check if the query matches the initial search text (exercise name)
        const matchesInitialText =
          initialSearchTextRef.current &&
          trimmedQuery === initialSearchTextRef.current.trim();

        // Show suggestions/recently used if query is empty
        if (!trimmedQuery) {
          // Check for recently used exercises first
          if (block && suggestionContext) {
            const recentExercises = searchService.getRecentlyUsedExercises(
              block,
              suggestionContext,
              protocolSlot,
              currentWeek,
              slotExercise
            );
            if (recentExercises.length > 0) {
              setRecentlyUsedExercises(recentExercises);
              setResults([]);
              setIsSearching(false);
              setSelectedIndex(0);
              return;
            }
          }

          // Fall back to suggestions
          if (suggestionContext) {
            const suggestions = searchService.getSuggestions(
              suggestionContext,
              predefinedExercises,
              slotExercise
            );
            setResults(suggestions);
            setRecentlyUsedExercises([]);
          } else {
            setResults([]);
            setRecentlyUsedExercises([]);
          }
          setIsSearching(false);
          setSelectedIndex(0);
          return;
        }

        // If query matches initial exercise name, try recently used first, then search
        if (matchesInitialText) {
          if (block && suggestionContext) {
            const recentExercises = searchService.getRecentlyUsedExercises(
              block,
              suggestionContext,
              protocolSlot,
              currentWeek,
              slotExercise
            );
            if (recentExercises.length > 0) {
              setRecentlyUsedExercises(recentExercises);
              setResults([]);
              setIsSearching(false);
              setSelectedIndex(0);
              return;
            }
          }
          // If no recently used, fall through to normal search below
        }

        try {
          const searchResults = searchService.search(
            trimmedQuery,
            predefinedExercises,
            predefinedAttributes,
            {
              context: suggestionContext,
              block,
              protocolSlot,
              currentWeek,
              currentExercise: slotExercise,
            }
          );
          setResults(searchResults.slice(0, 15)); // Limit to 15 results
          setRecentlyUsedExercises([]);
          setSelectedIndex(0);
        } catch (error) {
          console.error("Exercise search error:", error);
          setResults([]);
          setRecentlyUsedExercises([]);
        }

        setIsSearching(false);
      },
      [
        predefinedExercises,
        predefinedAttributes,
        searchService,
        suggestionContext,
        block,
      ]
    );

    // Handle search query change with debounce
    const handleSearchChange = useCallback(
      (text) => {
        setSearchQuery(text);

        if (debounceTimerRef.current) {
          clearTimeout(debounceTimerRef.current);
        }

        // Check if text matches initial exercise name
        const matchesInitialText =
          initialSearchTextRef.current &&
          text.trim() === initialSearchTextRef.current.trim();

        // Show suggestions/recently used if text is empty
        if (!text.trim()) {
          // Check for recently used exercises first
          if (block && suggestionContext) {
            const recentExercises = searchService.getRecentlyUsedExercises(
              block,
              suggestionContext,
              protocolSlot,
              currentWeek,
              slotExercise
            );
            if (recentExercises.length > 0) {
              setRecentlyUsedExercises(recentExercises);
              setResults([]);
              setIsSearching(false);
              setSelectedIndex(0);
              return;
            }
          }

          // Fall back to suggestions
          if (suggestionContext) {
            const suggestions = searchService.getSuggestions(
              suggestionContext,
              predefinedExercises,
              slotExercise
            );
            setResults(suggestions);
            setRecentlyUsedExercises([]);
          } else {
            setResults([]);
            setRecentlyUsedExercises([]);
          }
          setIsSearching(false);
          setSelectedIndex(0);
          return;
        }

        // If text matches initial exercise name, try recently used first
        // If no recently used, the debounced search will show search results
        if (matchesInitialText && block && suggestionContext) {
          const recentExercises = searchService.getRecentlyUsedExercises(
            block,
            suggestionContext,
            protocolSlot,
            currentWeek,
            slotExercise
          );
          if (recentExercises.length > 0) {
            setRecentlyUsedExercises(recentExercises);
            setResults([]);
            setIsSearching(false);
            setSelectedIndex(0);
            return;
          }
        }

        setIsSearching(true);

        debounceTimerRef.current = setTimeout(() => {
          performSearch(text);
        }, 100);
      },
      [
        performSearch,
        suggestionContext,
        searchService,
        predefinedExercises,
        block,
      ]
    );

    // Handle keyboard navigation
    const handleKeyDown = useCallback(
      (e) => {
        const totalItems =
          recentlyUsedExercises.length > 0
            ? recentlyUsedExercises.length
            : results.length;

        switch (e.key) {
          case "ArrowDown":
            if (totalItems > 0) {
              e.preventDefault();
              keyboardNavigationRef.current = true;
              setSelectedIndex((prev) =>
                prev < totalItems - 1 ? prev + 1 : 0
              );
            }
            break;
          case "ArrowUp":
            if (totalItems > 0) {
              e.preventDefault();
              keyboardNavigationRef.current = true;
              setSelectedIndex((prev) =>
                prev > 0 ? prev - 1 : totalItems - 1
              );
            }
            break;
          case "Enter":
            if (
              recentlyUsedExercises.length > 0 &&
              recentlyUsedExercises[selectedIndex]
            ) {
              e.preventDefault();
              handleSelectRecentlyUsed(recentlyUsedExercises[selectedIndex]);
            } else if (results.length > 0 && results[selectedIndex]) {
              e.preventDefault();
              handleSelectResult(results[selectedIndex]);
            }
            break;
          case "Escape":
            e.preventDefault();
            resetAndClose();
            break;
        }
      },
      [results, recentlyUsedExercises, selectedIndex, resetAndClose]
    );

    // Allow keyboard navigation/select/close even when the TextInput isn't focused.
    // Do not hijack keys while:
    // - delete confirmation is shown (it has its own handler)
    // - manual picker is open (it likely manages its own keyboard interactions)
    // - user is actively typing in an editable element
    useEffect(() => {
      if (!isOpen || showDeleteConfirmation || showManualPicker) {
        return;
      }

      if (typeof window === "undefined") {
        return;
      }

      const onKeyDown = (e) => {
        const key = e?.key;
        if (
          key !== "ArrowDown" &&
          key !== "ArrowUp" &&
          key !== "Enter" &&
          key !== "Escape"
        ) {
          return;
        }

        const floatingEl = refs?.floating?.current;
        const isInsideFloating =
          !!floatingEl && typeof floatingEl.contains === "function"
            ? floatingEl.contains(e?.target)
            : false;

        // If the user is typing in *another* input outside this floating UI,
        // don't hijack arrow/enter/escape.
        if (!isInsideFloating && isEditableEventTarget(e?.target)) {
          return;
        }

        // Capture + preventDefault keeps behavior consistent regardless of focus.
        e?.preventDefault?.();
        e?.stopPropagation?.();
        handleKeyDown(e);
      };

      window.addEventListener("keydown", onKeyDown, true);
      return () => window.removeEventListener("keydown", onKeyDown, true);
    }, [handleKeyDown, isOpen, refs, showDeleteConfirmation, showManualPicker]);

    const handleMouseMove = useCallback(() => {
      if (!isMobile) {
        keyboardNavigationRef.current = false;
      }
    }, [isMobile]);

    // Handle selecting a result
    const handleSelectResult = useCallback(
      (result) => {
        const fullName = buildExerciseNameWithModifiers(
          result.exerciseName,
          result.modifiers,
          language
        );
        onSelectExercise?.({
          exerciseId: result.exerciseId,
          exerciseOriginalName: result.exerciseOriginalName,
          exerciseName: fullName,
          exerciseType: result.exerciseType,
          modifiers: result.modifiers,
        });
        setSearchQuery("");
        setResults([]);
        setRecentlyUsedExercises([]);
        setSelectedIndex(0);
        onClose();
      },
      [onSelectExercise, onClose, language]
    );

    // Handle selecting a recently used exercise (directly select it)
    const handleSelectRecentlyUsed = useCallback(
      (exercise) => {
        // IMPORTANT: handleSelectResult() will build the full display name by
        // appending modifiers via buildExerciseNameWithModifiers().
        // Exercise.getName() already includes modifiers, so using it here would
        // duplicate them (e.g. "Low Bar Low Bar Squat").
        const result = {
          exerciseId: exercise.id,
          exerciseOriginalName: exercise.originalName,
          exerciseName: t(exercise.originalName),
          exerciseType: exercise.type || "",
          modifiers: (exercise.attributes || []).map((attr) => ({
            id: attr.id,
            originalName: attr.name,
            name: attr.getName ? attr.getName(t, language) : t(attr.name),
            type: attr.title,
            subtype: attr.subtitle || null,
          })),
        };

        // Call the same handler as regular results
        handleSelectResult(result);
      },
      [t, language, handleSelectResult]
    );

    const handleOpenManualPicker = useCallback(() => {
      setShowDeleteConfirmation(false);
      setShowManualPicker(true);
    }, []);

    const handleCloseManualPicker = useCallback(() => {
      setShowManualPicker(false);
    }, []);

    // Handle delete exercise
    const handleDeleteExercise = useCallback(() => {
      setDeleteButtonHovered(false);
      setShowDeleteConfirmation(true);
    }, []);

    // Confirm delete action
    const handleConfirmDelete = useCallback(() => {
      if (!slotExercise || !block) {
        setShowDeleteConfirmation(false);
        onClose();
        return;
      }

      if (slotExercise.workout) {
        block.deleteExerciseAndSlot(slotExercise);
      } else {
        // Set the selected slot before deleting
        block.setProperty("selectedSlotId", slotExercise.id.toLowerCase());
        block.deleteProtocol();
      }

      setShowDeleteConfirmation(false);
      onClose();
    }, [slotExercise, block, onClose]);

    // Cancel delete
    const handleCancelDelete = useCallback(() => {
      setShowDeleteConfirmation(false);
    }, []);

    // Keyboard shortcuts while delete confirmation is showing:
    // - Enter: confirm delete
    // - Escape: cancel delete (go back)
    useEffect(() => {
      if (!isOpen || !showDeleteConfirmation) {
        return;
      }

      const onKeyDown = (e) => {
        const key = e?.key;
        if (key !== "Enter" && key !== "Escape") {
          return;
        }

        e?.preventDefault?.();
        e?.stopPropagation?.();

        if (key === "Enter") {
          handleConfirmDelete();
          return;
        }

        handleCancelDelete();
      };

      // Capture to beat any parent/global Escape handlers.
      window.addEventListener("keydown", onKeyDown, true);
      return () => window.removeEventListener("keydown", onKeyDown, true);
    }, [
      handleCancelDelete,
      handleConfirmDelete,
      isOpen,
      showDeleteConfirmation,
    ]);

    // Cleanup debounce timer
    useEffect(() => {
      return () => {
        if (debounceTimerRef.current) {
          clearTimeout(debounceTimerRef.current);
        }
      };
    }, []);

    if (!isOpen) return null;

    const hasQuery = searchQuery.trim().length > 0;
    const hasResults = results.length > 0;
    const hasRecentlyUsed = recentlyUsedExercises.length > 0;

    const shouldStabilizeWidth =
      !showManualPicker && hasQuery && !hasResults && !hasRecentlyUsed;
    const emptyStateStableWidthStyle = shouldStabilizeWidth
      ? {
          width: isMobile ? "92vw" : 360,
          maxWidth: isMobile ? 420 : 360,
        }
      : null;

    // Determine delete confirmation message
    const deleteTitle = t("deleteProtocolAlertTitle-string");
    const deleteText = slotExercise?.workout
      ? t("deleteProtocolFromWorkoutAlertText-string", {
          workoutName: slotExercise.workout.name,
          itemName: slotExercise.getName(t, i18n.language),
        })
      : t("deleteProtocolAlertText-string", {
          itemName: slotExercise?.getName(t, i18n.language) || "",
        });

    return (
      <FloatingPortal>
        <FloatingFocusManager
          context={context}
          modal={false}
          // Prevent auto-focusing the first tabbable element (TextInput) on mobile,
          // which triggers the virtual keyboard and can cause a flicker.
          initialFocus={isMobile ? -1 : 0}
        >
          <View
            ref={refs.setFloating}
            style={[
              floatingStyles,
              floatingContainerStyle,
              emptyStateStableWidthStyle,
            ]}
            {...getFloatingProps()}
          >
            {showDeleteConfirmation ? (
              <DeleteConfirmationView
                deleteTitle={deleteTitle}
                deleteText={deleteText}
                onCancel={handleCancelDelete}
                onConfirm={handleConfirmDelete}
                t={t}
                isMobile={isMobile}
              />
            ) : showManualPicker ? (
              /* Manual Picker View */
              <ExerciseManualPickerModal
                onBack={handleCloseManualPicker}
                initialSearchQuery={searchQuery}
                block={block}
                slotExercise={slotExercise}
                primarySlotExercise={primarySlotExercise}
                protocolSlot={protocolSlot}
                currentWeek={currentWeek}
                isSecondProtocol={instanceId?.endsWith("-second")}
                onClose={() => {
                  setSearchQuery("");
                  setResults([]);
                  setRecentlyUsedExercises([]);
                  setSelectedIndex(0);
                  setShowManualPicker(false);
                  onClose();
                }}
                onSelectExercise={(payload) => {
                  onSelectExercise?.(payload);
                }}
              />
            ) : (
              /* Search View */
              <>
                {/* Search Input Header */}
                <View
                  style={{
                    flexDirection: "row",
                    alignItems: "center",
                    padding: 12,
                    backgroundColor: Colors.whiteColor,
                    borderBottomWidth: hasResults ? 0.5 : 0,
                    borderBottomColor: Colors.borderColor,
                    gap: 10,
                    outlineStyle: "none",
                  }}
                >
                  <View
                    style={{
                      flex: 1,
                      flexDirection: "row",
                      alignItems: "center",
                      backgroundColor: Colors.lightGrayColor,
                      borderRadius: 10,
                      borderWidth: 0.5,
                      borderColor: Colors.borderColor,
                      paddingHorizontal: 10,
                      height: 36,
                    }}
                  >
                    {/* <Ionicons
                  name="search"
                  size={14}
                  color={Colors.grayColor}
                  style={{ marginRight: 8 }}
                /> */}
                    <TextInput
                      ref={searchInputRef}
                      value={searchQuery}
                      onChangeText={handleSearchChange}
                      onKeyPress={handleKeyDown}
                      placeholder={t("searchExercise-string")}
                      placeholderTextColor={Colors.grayColor}
                      style={{
                        flex: 1,
                        minWidth: 0,
                        flexShrink: 1,
                        fontSize: isMobile ? 16 : 13,
                        color: Colors.blackColor,
                        paddingVertical: 0,
                        height: "100%",
                        outlineStyle: "none",
                      }}
                      autoCorrect={false}
                      autoCapitalize="none"
                    />

                    {/* Clear icon (only when there is text) */}
                    {searchQuery.length > 0 && (
                      <TouchableOpacity
                        onPress={() => {
                          handleSearchChange("");
                          searchInputRef.current?.focus();
                        }}
                        onMouseEnter={() =>
                          !isMobile && setClearIconHovered(true)
                        }
                        onMouseLeave={() =>
                          !isMobile && setClearIconHovered(false)
                        }
                        style={{
                          padding: 2,
                          marginLeft: 6,
                        }}
                      >
                        <Ionicons
                          name="close-circle-outline"
                          size={14}
                          color={
                            clearIconHovered
                              ? Colors.darkgrayColor
                              : Colors.grayColor
                          }
                        />
                      </TouchableOpacity>
                    )}

                    {/* Manual selection icon (always visible) */}
                    <Tooltip
                      content={t("selectManually-string")}
                      placement="top"
                      delayedOpen={true}
                    >
                      <TouchableOpacity
                        onPress={handleOpenManualPicker}
                        onMouseEnter={() =>
                          !isMobile && setManualIconHovered(true)
                        }
                        onMouseLeave={() =>
                          !isMobile && setManualIconHovered(false)
                        }
                        style={{
                          padding: 2,
                          marginLeft: 6,
                        }}
                      >
                        <Ionicons
                          name="list-outline"
                          size={16}
                          color={
                            manualIconHovered
                              ? Colors.darkgrayColor
                              : Colors.grayColor
                          }
                        />
                      </TouchableOpacity>
                    </Tooltip>
                  </View>

                  {/* Delete Button (only if initialSearchText exists) */}
                  {initialSearchText != "" && initialSearchText != null && (
                    <Tooltip
                      content={t("deleteExercise-string")}
                      placement="top"
                      delayedOpen={true}
                    >
                      <TouchableOpacity
                        onPress={handleDeleteExercise}
                        onMouseEnter={() =>
                          !isMobile && setDeleteButtonHovered(true)
                        }
                        onMouseLeave={() =>
                          !isMobile && setDeleteButtonHovered(false)
                        }
                        style={{
                          padding: 4,
                          justifyContent: "center",
                          alignItems: "center",
                          marginRight: 4,
                        }}
                      >
                        <Ionicons
                          name="trash-outline"
                          size={18}
                          color={
                            deleteButtonHovered
                              ? Colors.redColor
                              : Colors.grayColor
                          }
                        />
                      </TouchableOpacity>
                    </Tooltip>
                  )}

                  <TouchableOpacity
                    onPress={onClose}
                    onMouseEnter={() =>
                      !isMobile && setCloseButtonHovered(true)
                    }
                    onMouseLeave={() =>
                      !isMobile && setCloseButtonHovered(false)
                    }
                    style={{
                      padding: 4,
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    <Ionicons
                      name="close"
                      size={20}
                      color={
                        closeButtonHovered
                          ? Colors.darkgrayColor
                          : Colors.grayColor
                      }
                    />
                  </TouchableOpacity>
                </View>

                {/* Results List */}
                {hasRecentlyUsed && (
                  <ScrollView
                    ref={scrollViewRef}
                    onMouseMove={handleMouseMove}
                    style={{
                      flex: 1,
                      outlineStyle: "none",
                      borderTopWidth: 1,
                      borderTopColor: Colors.borderColor,
                    }}
                    contentContainerStyle={{
                      paddingTop: 12,
                      paddingBottom: 8,
                      outlineStyle: "none",
                    }}
                    keyboardShouldPersistTaps="handled"
                  >
                    {/* Section Header */}
                    <View
                      style={{
                        paddingHorizontal: 14,
                        marginHorizontal: 8,
                        // paddingTop: 4,
                        paddingBottom: 8,
                        flexDirection: "row",
                        alignItems: "center",
                        gap: 6,
                      }}
                    >
                      <Text
                        style={{
                          fontSize: 11,
                          fontWeight: "600",
                          color: Colors.grayColor,
                          textTransform: "uppercase",
                          letterSpacing: 0.5,
                        }}
                      >
                        {t("recentlyUsed-string")}
                      </Text>
                    </View>
                    {recentlyUsedExercises.map((exercise, index) => (
                      <RecentlyUsedRow
                        key={exercise.id}
                        exercise={exercise}
                        isSelected={index === selectedIndex}
                        onSelect={() => handleSelectRecentlyUsed(exercise)}
                        onHover={() => {
                          if (!keyboardNavigationRef.current) {
                            setSelectedIndex(index);
                          }
                        }}
                        t={t}
                        language={language}
                        index={index}
                        totalResults={recentlyUsedExercises.length}
                        scrollContainerRef={scrollViewRef}
                        keyboardNavigationRef={keyboardNavigationRef}
                      />
                    ))}
                  </ScrollView>
                )}
                {hasResults && (
                  <ScrollView
                    ref={scrollViewRef}
                    onMouseMove={handleMouseMove}
                    style={{
                      flex: 1,
                      outlineStyle: "none",
                    }}
                    contentContainerStyle={{
                      paddingVertical: 8,
                    }}
                    keyboardShouldPersistTaps="handled"
                  >
                    {results.map((result, index) => (
                      <SuggestionRow
                        key={`${result.exerciseId}-${result.modifiers
                          .map((m) => m.id)
                          .join("-")}-${index}`}
                        result={result}
                        isSelected={index === selectedIndex}
                        onSelect={() => handleSelectResult(result)}
                        onHover={() => {
                          if (!keyboardNavigationRef.current) {
                            setSelectedIndex(index);
                          }
                        }}
                        searchQuery={searchQuery}
                        t={t}
                        language={language}
                        index={index}
                        totalResults={results.length}
                        scrollContainerRef={scrollViewRef}
                        keyboardNavigationRef={keyboardNavigationRef}
                      />
                    ))}

                    {/* Footer hint at end of list */}
                    <View
                      style={{
                        paddingVertical: 12,
                        paddingHorizontal: 12,
                        alignItems: "center",
                        // marginTop: 8,
                      }}
                    >
                      <Text
                        style={{
                          fontSize: 11,
                          color: Colors.grayColor,
                          textAlign: "center",
                        }}
                      >
                        {t("notFindingWhatYouWant-string")}{" "}
                        <Text
                          onPress={handleOpenManualPicker}
                          onMouseEnter={() =>
                            !isMobile && setManualLinkFooterHovered(true)
                          }
                          onMouseLeave={() =>
                            !isMobile && setManualLinkFooterHovered(false)
                          }
                          style={{
                            color: manualLinkFooterHovered
                              ? MANUAL_HOVER_COLOR
                              : Colors.bubbleColor,
                            fontWeight: "600",
                            cursor: "pointer",
                          }}
                        >
                          {t("selectExerciseManually-string")}
                        </Text>
                      </Text>
                    </View>
                  </ScrollView>
                )}

                {/* Searching Placeholder (keeps panel content stable during debounce) */}
                {hasQuery && !hasResults && !hasRecentlyUsed && isSearching && (
                  <View
                    style={{
                      flex: 1,
                      paddingVertical: 26,
                      // marginTop: 4,
                      paddingHorizontal: 24,
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 10,
                    }}
                  >
                    <CustomActivityIndicator large={false} white={false} />
                  </View>
                )}

                {/* No Results */}
                {hasQuery &&
                  !hasResults &&
                  !hasRecentlyUsed &&
                  !isSearching && (
                    <View
                      style={{
                        paddingVertical: 26,
                        marginTop: 8,
                        paddingHorizontal: 24,
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 10,
                      }}
                    >
                      <Text
                        style={{
                          fontSize: 13,
                          color: Colors.grayColor,
                          lineHeight: 18,
                          textAlign: "center",
                          maxWidth: "100%",
                        }}
                      >
                        {t("notFindingWhatYouWant-string")}{" "}
                        <Text
                          onPress={handleOpenManualPicker}
                          onMouseEnter={() =>
                            !isMobile && setManualLinkEmptyHovered(true)
                          }
                          onMouseLeave={() =>
                            !isMobile && setManualLinkEmptyHovered(false)
                          }
                          style={{
                            color: manualLinkEmptyHovered
                              ? MANUAL_HOVER_COLOR
                              : Colors.bubbleColor,
                            fontWeight: "600",
                            cursor: "pointer",
                            flexShrink: 1,
                          }}
                        >
                          {t("selectExerciseManually-string")}
                        </Text>
                      </Text>
                    </View>
                  )}

                {/* Footer hint + keyboard hint */}
                {(hasResults || hasRecentlyUsed) && !isMobile && (
                  <View
                    style={{
                      paddingVertical: 8,
                      paddingHorizontal: 12,
                      borderTopWidth: 0.5,
                      borderTopColor: Colors.borderColor,
                      outlineStyle: "none",
                    }}
                  >
                    <Text
                      style={{
                        fontSize: 10,
                        color: Colors.grayColor,
                        textAlign: "center",
                      }}
                    >
                      ↑↓ {t("navigate-string")}
                      {"  •  "}↵ {t("select-string")}
                      {"  •  "}Esc {t("close-string")}
                    </Text>
                  </View>
                )}
              </>
            )}
          </View>
        </FloatingFocusManager>
      </FloatingPortal>
    );
  }
);

export default ExerciseSearchFloating;
